import { httpClient } from '@/shared/api/httpClient';

import { ownerlessPageSchema, ownerlessSummarySchema } from '../model/types';
import type { OwnerlessPage, OwnerlessSummary } from '../model/types';

/** Podsumowanie listy roboczej — dwa kubełki, każdy pogrupowany po źródle dziedziczenia. */
export async function fetchOwnerless(
  importRunId: number | null,
  signal?: AbortSignal,
): Promise<OwnerlessSummary> {
  const query = importRunId === null ? '' : `?importRunId=${importRunId}`;

  return ownerlessSummarySchema.parse(
    await httpClient.get<unknown>(`/elements/ownerless${query}`, { ...(signal ? { signal } : {}) }),
  );
}

/**
 * Elementy jednej gałęzi.
 *
 * ⚠ `sourceElementId === null` to **kubełek „nikt"**, a nie „wszystko" — dlatego parametr
 * jest wysyłany tylko wtedy, gdy gałąź ma źródło.
 */
export async function fetchOwnerlessBranch(
  sourceElementId: number | null,
  options: { importRunId?: number | null; includeReviewed?: boolean; offset?: number; limit?: number },
  signal?: AbortSignal,
): Promise<OwnerlessPage> {
  const query = new URLSearchParams();

  if (sourceElementId !== null) query.set('sourceElementId', String(sourceElementId));
  if (options.importRunId !== null && options.importRunId !== undefined) query.set('importRunId', String(options.importRunId));
  if (options.includeReviewed) query.set('includeReviewed', 'true');
  if (options.offset !== undefined) query.set('offset', String(options.offset));
  if (options.limit !== undefined) query.set('limit', String(options.limit));

  const suffix = query.size > 0 ? `?${query.toString()}` : '';

  return ownerlessPageSchema.parse(
    await httpClient.get<unknown>(`/elements/ownerless/branch${suffix}`, {
      ...(signal ? { signal } : {}),
    }),
  );
}

/**
 * „Przyjmuję dziedziczenie".
 *
 * ⛔ **Nie zapisuje właściciela** — dziedziczenie po `has` działa samo, więc nie ma czego
 * wstawiać. Zapisuje ślad, że człowiek to obejrzał; bez niego ta sama lista wraca nazajutrz
 * w komplecie.
 */
export async function acceptInherited(input: {
  elementIds?: readonly number[];
  branchRootId?: number;
}): Promise<number> {
  return httpClient.post<number>('/elements/ownerless/accept', {
    elementIds: input.elementIds ?? null,
    branchRootId: input.branchRootId ?? null,
  });
}
